import { lazy, Suspense } from 'react';
import { PauseCircle } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { Button, Skeleton } from '../components/ui';

const PlanCredits = lazy(() => import('./tenant/PlanCredits'));

export function AccountPaused() {
  const { me, logout, can } = useAuth();
  const reason = me?.tenant?.pauseReason;
  const renewable = reason === 'trial_ended' || reason === 'renewal_due';

  // Someone who can pay sees the plans right here, so one payment brings everyone back.
  if (renewable && can('billing.manage')) {
    return (
      <div className="min-h-full">
        <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-900">
          <span className="font-semibold">{me?.tenant?.name}</span>
          <Button variant="ghost" size="sm" onClick={logout}>Sign out</Button>
        </header>
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          <Suspense fallback={<Skeleton className="h-96" />}><PlanCredits /></Suspense>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
        <PauseCircle className="mx-auto size-12 text-amber-500" />
        <h1 className="mt-4 text-xl font-semibold">
          {reason === 'trial_ended' ? 'Your free trial has ended' : reason === 'renewal_due' ? 'Your plan has ended' : `${me?.tenant?.name ?? 'Your company'}’s account is paused`}
        </h1>
        <p className="mt-2 text-slate-600 dark:text-slate-400">
          Don’t worry — all your data is safe.{' '}
          {renewable
            ? 'Please ask your company admin to renew the plan, then sign in again.'
            : me?.role?.key === 'admin'
              ? 'Please contact AI CRM support to continue using the app.'
              : 'Please ask your company admin to contact AI CRM support.'}
        </p>
        <Button variant="secondary" className="mt-6" onClick={logout}>Sign out</Button>
      </div>
    </div>
  );
}
